import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

const SettingsSearchContext = createContext({
  query: '',
  setQuery: () => {},
  registerMatch: () => () => {},
  matchCounts: {},
});

/**
 * Provider for the Settings search state.
 *
 * - `query` is the current search string (lowercase, trimmed).
 * - `setQuery` updates it.
 * - `registerMatch(tabId, hasMatch)` lets each CollapsibleSettingsCard report
 *   whether it matches the current query. We aggregate counts per tab so the
 *   tab strip can render badge counts ("Account 2"). Returns a cleanup fn.
 */
export function SettingsSearchProvider({ children }) {
  const [query, setQueryRaw] = useState('');
  const setQuery = useCallback((v) => setQueryRaw((v || '').toLowerCase().trim()), []);

  // tabId -> Map<cardKey, hasMatch>
  const [registry, setRegistry] = useState({});

  const registerMatch = useCallback((tabId, cardKey, hasMatch) => {
    setRegistry((prev) => {
      const tab = { ...(prev[tabId] || {}) };
      if (hasMatch) tab[cardKey] = true;
      else delete tab[cardKey];
      return { ...prev, [tabId]: tab };
    });
    return () => {
      setRegistry((prev) => {
        const tab = { ...(prev[tabId] || {}) };
        delete tab[cardKey];
        return { ...prev, [tabId]: tab };
      });
    };
  }, []);

  const matchCounts = useMemo(() => {
    const out = {};
    Object.entries(registry).forEach(([tabId, cards]) => {
      out[tabId] = Object.keys(cards).length;
    });
    return out;
  }, [registry]);

  const value = useMemo(
    () => ({ query, setQuery, registerMatch, matchCounts }),
    [query, setQuery, registerMatch, matchCounts],
  );

  return (
    <SettingsSearchContext.Provider value={value}>
      {children}
    </SettingsSearchContext.Provider>
  );
}

export function useSettingsSearch() {
  return useContext(SettingsSearchContext);
}

/**
 * Hook for individual collapsible cards. Returns `{ matches, isSearching }`
 * and registers/unregisters with the parent registry for tab badge counts.
 */
export function useSettingsSearchMatch(tabId, cardKey, haystack) {
  const { query, registerMatch } = useSettingsSearch();
  const matches = useMemo(() => {
    if (!query) return false;
    const hay = (haystack || '').toString().toLowerCase();
    return hay.includes(query);
  }, [query, haystack]);

  React.useEffect(() => {
    if (!query) return undefined;
    return registerMatch(tabId, cardKey, matches);
  }, [tabId, cardKey, matches, query, registerMatch]);

  return { matches, isSearching: !!query };
}
